import React from 'react';
import { Camera, Calculator, MessageSquare, MapPin, Sparkles, Car } from 'lucide-react';

/**
 * CE QUE LE VISITEUR A ENVOYÉ (2026-09-20) — affiché en tête d'un dossier né du simulateur du site.
 *
 * C'est l'écran sur lequel l'établissement RÉÉTUDIE le tarif : la sélection telle qu'elle a été simulée,
 * l'estimation que le visiteur a vue (indicative, jamais un engagement), ses photos rangées par prestation,
 * son adresse et son message. Les photos arrivent en clair depuis la route dédiée, jamais dans les listes.
 *
 * Les données sont chargées par la page parente (qui s'en sert aussi pour préremplir le devis) : ce
 * composant ne fait qu'afficher, pour rester lisible et ne jamais recharger deux fois les mêmes images.
 */

export interface OrigineDemande {
    source: string;
    estimation: { lignes: { id: string; qty: number; label: string; unit_type: string; unit_price_ttc: number }[]; total_ttc: number; created_at: string } | null;
    photos: { id: string; name: string; data_url: string }[];
    message: string;
    adresse: string;
    formule: { id: string; name: string; occurrences_total: number; passages_libres?: boolean; validity_months: number } | null;
    km_fee: { amount: number; waived: boolean; distance_km: number; aller_retour: boolean } | null;
    // Renseigné pour un professionnel quand l'établissement l'a demandé.
    infos_pro?: { type_etablissement?: string; contact_fonction?: string; siret?: string } | null;
}

const eur = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })} €`;
const uniteLabel = (u: string) => (u === 'm2' ? 'm²' : u === 'heure' ? 'h' : u === 'ml' ? 'ml' : '×');

export function OrigineDemandeDevis({ origine }: { origine: OrigineDemande }) {
    const [agrandie, setAgrandie] = React.useState<string | null>(null);
    if (origine.source !== 'simulateur') return null;

    return (
        <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
            {agrandie && (
                <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setAgrandie(null)}>
                    <img src={agrandie} alt="" className="max-w-full max-h-full rounded-xl object-contain" />
                </div>
            )}

            <div>
                <p className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Demande reçue depuis le simulateur</p>
                <p className="text-sm text-content-muted mt-1">
                    Ce que le client a simulé lui-même. Le montant ci-dessous est ce qu'il a vu à l'écran, à titre indicatif :
                    c'est vous qui arrêtez le tarif réel dans le devis.
                </p>
            </div>

            {origine.estimation && (
                <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Calculator size={12} /> Sa sélection</p>
                    {origine.estimation.lignes.map((l) => (
                        <div key={l.id} className="flex items-center justify-between gap-3 text-sm">
                            <span className="text-content">{l.label}</span>
                            <span className="text-content-muted tabular-nums shrink-0">{l.qty} {uniteLabel(l.unit_type)} · {eur(l.qty * l.unit_price_ttc)}</span>
                        </div>
                    ))}
                    <div className="pt-1.5 mt-1.5 border-t border-border flex items-center justify-between text-sm">
                        <span className="font-bold text-content">Estimation affichée</span>
                        <span className="font-black text-content tabular-nums">{eur(origine.estimation.total_ttc)} TTC</span>
                    </div>
                </div>
            )}

            {origine.km_fee && (
                <div className={`flex items-center gap-2 text-sm font-bold rounded-xl px-3 py-2.5 border ${origine.km_fee.waived
                    ? 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30'
                    : 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30'}`}>
                    <Car size={16} className="shrink-0" />
                    {origine.km_fee.waived
                        ? <span>Frais kilométriques ({origine.km_fee.distance_km} km {origine.km_fee.aller_retour ? 'aller-retour' : 'aller'}) : <b>offerts</b> vus par le client.</span>
                        : <span>Frais kilométriques vus par le client : <b>environ {eur(origine.km_fee.amount)}</b> ({origine.km_fee.distance_km} km {origine.km_fee.aller_retour ? 'aller-retour' : 'aller'}).</span>}
                </div>
            )}

            {origine.formule && (
                <p className="text-sm text-content flex items-center gap-1.5">
                    <Sparkles size={13} className="text-brand-600 dark:text-brand-400" />
                    Formule <b>{origine.formule.name}</b> retenue — {origine.formule.passages_libres ? 'passages libres' : `${origine.formule.occurrences_total} passages`} sur {origine.formule.validity_months} mois.
                </p>
            )}

            {origine.photos.length > 0 && (
                <div className="space-y-2">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                        <Camera size={12} /> {origine.photos.length} photo{origine.photos.length > 1 ? 's' : ''} jointe{origine.photos.length > 1 ? 's' : ''}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {origine.photos.map((p) => (
                            <button key={p.id} type="button" onClick={() => setAgrandie(p.data_url)}
                                className="text-left rounded-xl overflow-hidden border border-border bg-surface-soft hover:border-brand-500 transition-colors">
                                <img src={p.data_url} alt={p.name} className="w-full aspect-[4/3] object-cover" />
                                <span className="block px-2 py-1.5 text-[11px] text-content-muted truncate">{p.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {origine.infos_pro && (origine.infos_pro.type_etablissement || origine.infos_pro.contact_fonction || origine.infos_pro.siret) && (
                <div className="p-3 rounded-xl bg-surface-soft border border-border text-sm text-content space-y-0.5">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1">Professionnel</p>
                    {origine.infos_pro.type_etablissement && <p>Type d'établissement : <b>{origine.infos_pro.type_etablissement}</b></p>}
                    {origine.infos_pro.contact_fonction && <p>Fonction du contact : <b>{origine.infos_pro.contact_fonction}</b></p>}
                    {origine.infos_pro.siret && <p>SIRET : <b>{origine.infos_pro.siret}</b></p>}
                </div>
            )}

            {origine.adresse && (
                <p className="text-sm text-content-muted flex items-center gap-1.5"><MapPin size={13} /> {origine.adresse}</p>
            )}

            {origine.message && (
                <div className="p-3 rounded-xl bg-surface-soft border border-border">
                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5 mb-1"><MessageSquare size={12} /> Son message</p>
                    <p className="text-sm text-content whitespace-pre-wrap">{origine.message}</p>
                </div>
            )}
        </div>
    );
}
